import { FormControl, NG_ASYNC_VALIDATORS } from '@angular/forms';
import { Directive, EventEmitter, HostBinding, Input, OnDestroy, Output } from '@angular/core';
import { finalize, map, Observable, of, Subscription } from 'rxjs';
import Validator from 'validator';

import { UserCore } from '@reg/shared/model';
import { UserService } from '@reg/shared/service';
import { AuthService } from '@reg/shared/auth/auth.service';
import { HttpErrorResponse } from '@angular/common/http';

@Directive({
    selector: '[floAsyncUserCoreEmailValidator]',
    providers: [
        { provide: NG_ASYNC_VALIDATORS, useExisting: UserCoreEmailDirective, multi: true },
    ],
})
export class UserCoreEmailDirective implements OnDestroy {
    @Output() public isValidatingChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() public userCoreChange: EventEmitter<UserCore> = new EventEmitter<UserCore>();
    @Input() public invalidIfExists: boolean = true;
    @Input() public isUserEmailValid: boolean = false;
    @Input() public isUserCoreEmailRequired: boolean = true;

    private _isValidating: boolean = false;
    @HostBinding('class.is-validating')
    set isValidating(isValidating: boolean) {
        this._isValidating = isValidating;
        this.isValidatingChange.emit(isValidating);
    }

    get isValidating(): boolean {
        return this._isValidating;
    }

    private formControl: FormControl;
    private timeout: any;
    private defaultDelay: number = 600; // wait 600 milliseconds before validating so we do not validate on every key stroke
    private delay: number = this.defaultDelay;
    private valueChangedSubscription: Subscription;

    constructor(
                private userService: UserService,
                private authService: AuthService,
    ) {
    }

    public validate(control: FormControl): Observable<any> {
        clearTimeout(this.timeout);
        this.isValidating = true;
        this.formControl = control;

        if (!control.value) {
            this.userCoreChange.emit(null);
            this.isValidating = false;
            return of(null);
        }

        // Set a timeout so we don't make an http call for every key stroke.
        return new Observable((obs: any): void => {
            this.timeout = setTimeout(
                () => {
                    this.delay = this.defaultDelay;

                    this.getCoreUser(control.value)
                        .pipe(
                            map((userCore: UserCore) => {
                                if (!userCore && !this.isUserCoreEmailRequired) {
                                    // A user isn't required so this is valid
                                    this.userCoreChange.emit(null);
                                    return null;
                                }

                                if (!userCore &&
                                    !this.invalidIfExists) {
                                    // No user found and the component is flagged error if we can't find one
                                    return { emailNotFound: true };
                                }

                                if (!userCore ||
                                    !control.value) {
                                    // No user and no value so return null (valid)
                                    this.userCoreChange.emit(null);
                                    return null;
                                }

                                this.userCoreChange.emit(userCore);

                                if (userCore &&
                                    this.invalidIfExists) {
                                    // Found a user and the component is flagged to error if we found one

                                    if (this.isUserEmailValid &&
                                        this.authService.user &&
                                        this.authService.user.email === userCore.email) {
                                        // This is the user's current email so it's valid
                                        return null;
                                    }

                                    return { emailAlreadyExists: true };
                                }

                                return null;
                            }),
                            finalize(() => {
                                obs.complete();
                                this.isValidating = false;
                            }),
                        ).subscribe({
                            next: (data: any) => { obs.next(data); },
                            error: (error: HttpErrorResponse) => {
                                if (this.isUserCoreEmailRequired) {
                                    obs.next({ invalid: true });
                                } else {
                                    this.userCoreChange.emit(null);
                                    obs.next(null);
                                }
                            },
                        });
                },
                this.delay,
            );
        });
    }

    public ngOnDestroy(): void {
        if (this.valueChangedSubscription) {
            this.valueChangedSubscription.unsubscribe();
        }
    }

    private getCoreUser(searchValue: string): Observable<UserCore> {
        if (Validator.isInt(searchValue)) {
            // Search by the userId
            return this.userService.getCoreUser(searchValue);
        }

        if (Validator.isEmail(searchValue)) {
            // Search by email
            return this.userService.getCoreUserByEmail(searchValue);
        }

        return of(null);
    }
}
